import { COLORS } from "src/constants/colors-library";
import type { Zone } from "src/types/Zone";
import { ZoneType } from "src/types/Zone";

export interface StyleSheet {
  NONE: StyleSheetRecord;
  HOVER: StyleSheetRecord;
  SELECT: StyleSheetRecord;
  SELECT_HOVER: StyleSheetRecord;
}
interface StyleSheetRecord {
  FILL_COLOR: string | number;
  FILL_ALPHA: number;
  BORDER_COLOR: number | string;
  BORDER_ALPHA: number;
  BORDER_WIDTH: number;
  DASH_SIZE_PX?: number;
  GAP_SIZE_PX?: number;
}

export const ZONE_POLYGON_STYLE = {
  BUILDIABLE_OFFSET: {
    NONE: {
      FILL_COLOR: 0xfde7c0,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: 0x000000, // COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
      DASH_SIZE_PX: 4,
      GAP_SIZE_PX: 4,
    },
    HOVER: {
      FILL_COLOR: 0xfbcc7a,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: 0x000000, // COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
      DASH_SIZE_PX: 4,
      GAP_SIZE_PX: 4,
    },
    SELECT: {
      FILL_COLOR: 0xfde7c0,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: 0x000000, // COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
      DASH_SIZE_PX: 4,
      GAP_SIZE_PX: 4,
    },
    SELECT_HOVER: {
      FILL_COLOR: 0xfde7c0,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: 0x000000, // COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
      DASH_SIZE_PX: 4,
      GAP_SIZE_PX: 4,
    },
  },
  BUILDABLE: {
    NONE: {
      FILL_COLOR: 0xbedfdd,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
    },
    HOVER: {
      FILL_COLOR: 0xbabade,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
    },
    SELECT: {
      FILL_COLOR: 0x9e9ed1,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: 0x58588e,
      BORDER_ALPHA: 1,
      BORDER_WIDTH: 3,
    },
    SELECT_HOVER: {
      FILL_COLOR: 0xbabade,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: 0x58588e,
      BORDER_ALPHA: 1,
      BORDER_WIDTH: 3,
    },
  },
  EXCLUSION: {
    NONE: {
      FILL_COLOR: 0xf9ccd0,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
    },
    HOVER: {
      FILL_COLOR: 0xf6b3b9,
      FILL_ALPHA: 0.5,
      BORDER_COLOR: COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
    },
    SELECT: {
      FILL_COLOR: 0xf76470,
      FILL_ALPHA: 0.3,
      BORDER_COLOR: 0xf76470,
      BORDER_ALPHA: 1,
      BORDER_WIDTH: 3,
    },
    SELECT_HOVER: {
      FILL_COLOR: 0xf6b3b9,
      FILL_ALPHA: 0.5,
      BORDER_COLOR: 0xf76470,
      BORDER_ALPHA: 1,
      BORDER_WIDTH: 3,
    },
  },
  PARKING: {
    NONE: {
      FILL_COLOR: 0xcfd4da,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
    },
    HOVER: {
      FILL_COLOR: 0xb4bac1,
      FILL_ALPHA: 0.5,
      BORDER_COLOR: COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
    },
    SELECT: {
      FILL_COLOR: 0xb4bac1,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: 0x6b7280,
      BORDER_ALPHA: 1,
      BORDER_WIDTH: 3,
    },
    SELECT_HOVER: {
      FILL_COLOR: 0xb4bac1,
      FILL_ALPHA: 0.5,
      BORDER_COLOR: 0x6b7280,
      BORDER_ALPHA: 1,
      BORDER_WIDTH: 3,
    },
  },
  ACCESS: {
    NONE: {
      FILL_COLOR: 0xfde7c0,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
    },
    HOVER: {
      FILL_COLOR: 0xfbcc7a,
      FILL_ALPHA: 0.5,
      BORDER_COLOR: COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
    },
    SELECT: {
      FILL_COLOR: 0xfbcc7a,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: 0xb8791a,
      BORDER_ALPHA: 1,
      BORDER_WIDTH: 3,
    },
    SELECT_HOVER: {
      FILL_COLOR: 0xfbcc7a,
      FILL_ALPHA: 0.5,
      BORDER_COLOR: 0xb8791a,
      BORDER_ALPHA: 1,
      BORDER_WIDTH: 3,
    },
  },
  GREEN_ZONES: {
    NONE: {
      FILL_COLOR: 0xcfe8c9,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
    },
    HOVER: {
      FILL_COLOR: 0xa9d6a0,
      FILL_ALPHA: 0.5,
      BORDER_COLOR: COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
    },
    SELECT: {
      FILL_COLOR: 0xa9d6a0,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: 0x4f8a44,
      BORDER_ALPHA: 1,
      BORDER_WIDTH: 3,
    },
    SELECT_HOVER: {
      FILL_COLOR: 0xa9d6a0,
      FILL_ALPHA: 0.5,
      BORDER_COLOR: 0x4f8a44,
      BORDER_ALPHA: 1,
      BORDER_WIDTH: 3,
    },
  },
  WARNING: {
    NONE: {
      FILL_COLOR: COLORS.warning["4"],
      FILL_ALPHA: 0.4,
      BORDER_COLOR: COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
    },
    HOVER: {
      FILL_COLOR: COLORS.warning["4"],
      FILL_ALPHA: 0.7,
      BORDER_COLOR: COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
    },
    SELECT: {
      FILL_COLOR: COLORS.warning["5"],
      FILL_ALPHA: 0.4,
      BORDER_COLOR: COLORS.warning["6"],
      BORDER_ALPHA: 1,
      BORDER_WIDTH: 2,
    },
    SELECT_HOVER: {
      FILL_COLOR: COLORS.warning["5"],
      FILL_ALPHA: 0.6,
      BORDER_COLOR: COLORS.warning["6"],
      BORDER_ALPHA: 1,
      BORDER_WIDTH: 2,
    },
  },
  LOCKED: {
    NONE: {
      FILL_COLOR: 0xd0d6dd,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: COLORS.neutral["6"],
      BORDER_ALPHA: 0.7,
      BORDER_WIDTH: 1,
    },
    HOVER: {
      FILL_COLOR: 0xd0d6dd,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: 0x000000,
      BORDER_ALPHA: 0,
      BORDER_WIDTH: 0,
    },
    SELECT: {
      FILL_COLOR: 0xd0d6dd,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: COLORS.neutral["6"],
      BORDER_ALPHA: 1,
      BORDER_WIDTH: 1,
    },
    SELECT_HOVER: {
      FILL_COLOR: 0xd0d6dd,
      FILL_ALPHA: 0.4,
      BORDER_COLOR: COLORS.neutral["6"],
      BORDER_ALPHA: 1,
      BORDER_WIDTH: 1,
    },
  },
} satisfies Record<string, StyleSheet>;
export function GetZoneStyle(zone: Zone, enableWarnings: boolean) {
  if (zone.locked) return ZONE_POLYGON_STYLE.LOCKED;
  if (enableWarnings && zone.zoneType === ZoneType.buildable && !zone.hasBuilding)
    return ZONE_POLYGON_STYLE.WARNING;
  switch (zone.zoneType) {
    case ZoneType.buildable:
      return ZONE_POLYGON_STYLE.BUILDABLE;
    case ZoneType.parking:
      return ZONE_POLYGON_STYLE.PARKING;
    case ZoneType.access:
      return ZONE_POLYGON_STYLE.ACCESS;
    case ZoneType.green_zones:
      return ZONE_POLYGON_STYLE.GREEN_ZONES;
    case ZoneType.exclusion:
    default:
      return ZONE_POLYGON_STYLE.EXCLUSION;
  }
}
